import { Injectable } from '@angular/core';

import { environment } from './../../environments/environment.prod';
import { ApiService, AppContext, UserService, Register } from './../shared';

declare const AWS: any;
declare const AWSCognito: any;
declare const apigClientFactory: any;

@Injectable()
export class AuthService {
  private loginUrl = '';
  private registerUrl = '';
  private cognitoUser: any = {};
  private userPool: any = {};

  constructor(private api: ApiService, private app: AppContext, private user: UserService) {
  }

  login(email: string, password: string) {
    localStorage.removeItem('password'); // just in case it is set

    let userData = {
      Username: email.toLowerCase(),
      Pool: this.api.userPool
    };

    this.cognitoUser = new AWSCognito.CognitoIdentityServiceProvider.CognitoUser(userData);

    let authenticationData = {
      Username: email.toLowerCase(),
      Password: password,
    };

    let authenticationDetails = new AWSCognito.CognitoIdentityServiceProvider.AuthenticationDetails(authenticationData);

    return new Promise((resolve, reject) => {
      try {
        this.cognitoUser.authenticateUser(authenticationDetails, {
          onSuccess: (result) => {
            if (result) {
              // Add the User's Id Token to the Cognito credentials login map.
              AWS.config.credentials = new AWS.CognitoIdentityCredentials({
                IdentityId: AWS.config.credentials.identityId,
                IdentityPoolId: environment.identityPoolId,
                Logins: {
                  [`cognito-idp.${environment.region}.amazonaws.com/${environment.userPoolId}`]: result.getIdToken().getJwtToken()
                }
              });

              AWSCognito.config.credentials = new AWS.CognitoIdentityCredentials({
                IdentityId: result.identityId,
                IdentityPoolId: environment.identityPoolId,
                Logins: {
                  [`cognito-idp.${environment.region}.amazonaws.com/${environment.userPoolId}`]: result.getIdToken().getJwtToken()
                }
              });

              AWS.config.credentials.get(() => {
                this.api.client = apigClientFactory.newClient({
                  accessKey: AWS.config.credentials.accessKeyId,
                  secretKey: AWS.config.credentials.secretAccessKey,
                  sessionToken: AWS.config.credentials.sessionToken,
                  region: environment.region
                });
                this.app.setLogin(true);
                this.completeLogin(email.toLowerCase());
                resolve();
              });
            }
          },

          onFailure: function (err) {
            // console.log('logging in did not work', err);
            if (err == undefined || err.message == undefined) {
              let error = {
                message: 'Password attempts exceeded'
              };
              return reject(error);
            }
            return reject(err);
          },
          mfaRequired: function (codeDeliveryDetails) {
            let verificationCode = prompt('Please input verification code', '');
            this.cognitoUser.sendMFACode(verificationCode, this);
          }
        });
      } catch (ex) {
        return reject(ex);
      }
    });
  }

  completeLogin(email: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.user.checkIfUser(email).then(result => {
        // is a user, get profile
        this.user.getProfile();
        resolve();
      }).catch(ex => {
        // not a user, confirm account
        this.user.confirmUser(email).then(result => {
          this.user.getProfile();
          resolve();
        }).catch(confirmEx => {
          reject(confirmEx);
        });
      });
    });
  }

  getCurrentUser() {
    return this.api.userPool.getCurrentUser();
  }

  getCognitoUserAttributeList(model: Register): Array<any> {
    let attributeList = [];

    let dataEmail = {
      Name: 'email',
      Value: model.email.toLowerCase()
    };
    let attributeEmail = new AWSCognito.CognitoIdentityServiceProvider.CognitoUserAttribute(dataEmail);
    attributeList.push(attributeEmail);

    let dataFirstName = {
      Name: 'given_name',
      value: model.firstName
    };
    let attributeFirstName = new AWSCognito.CognitoIdentityServiceProvider.CognitoUserAttribute(dataFirstName);
    attributeList.push(attributeFirstName);

    let dataLastName = {
      Name: 'family_name',
      value: model.lastName
    };
    let attributeLastName = new AWSCognito.CognitoIdentityServiceProvider.CognitoUserAttribute(dataLastName);
    attributeList.push(attributeLastName);

    return attributeList;
  }

  register(model: Register) {
    let attributeList = this.getCognitoUserAttributeList(model);

    let userPool = this.api.userPool;

    return new Promise((resolve, reject) => {
      try {
        userPool.signUp(
          model.email.toLowerCase(),
          model.password,
          attributeList,
          null,
          (err, result) => {
            if (err !== null) {
              return reject(err);
            }
            resolve(result);
          }
        );
      } catch (ex) {
        return reject(ex);
      }
    });
  }

  confirmEmail(verificationCode: String, email: String) {
    let userPool = this.api.userPool;
    return new Promise((resolve, reject) => {
      try {
        let userData = {
          Username: email.toLowerCase(),
          Pool: userPool
        };

        this.cognitoUser = new AWSCognito.CognitoIdentityServiceProvider.CognitoUser(userData);
        this.cognitoUser.confirmRegistration(verificationCode, true, (err, result) => {
          if (err) {
            reject(err);
          }
          resolve(result);
        });
      } catch (ex) {
        return reject(ex);
      }
    });
  }

  resendVerificationEmail(email: string) {
    return new Promise((resolve, reject) => {
      try {
        let userData = {
          Username: email.toLowerCase(),
          Pool: this.api.userPool
        };
        this.cognitoUser = new AWSCognito.CognitoIdentityServiceProvider.CognitoUser(userData);

        this.cognitoUser.resendConfirmationCode((err, result) => {
          err ? reject(err) : resolve(result);
        });
      } catch (ex) {
        reject(ex);
      }
    });
  }

  changePassword(oldPassword: string, newPassword: string) {
    return new Promise((resolve, reject) => {
      try {
        // user needs to be authenticated
        this.login(this.app.email, oldPassword).then(result => {
          this.cognitoUser.changePassword(oldPassword, newPassword, (err, changeResult) => {
            err ? reject(err) : resolve(changeResult);
          });
        }).catch(err => {
          reject(err);
        });
      } catch (ex) {
        reject(ex);
      }
    });
  }

  forgotPassword(email: string): Promise<any> {
    return new Promise((resolve, reject) => {
      try {
        let userData = {
          Username: email.toLowerCase(),
          Pool: this.api.userPool
        };
        this.cognitoUser = new AWSCognito.CognitoIdentityServiceProvider.CognitoUser(userData);
        // console.log('AuthService forgotPassword');
        this.cognitoUser.forgotPassword({
          onSuccess: (result) => {
            // console.log('forgotPassword result', result);
            resolve(true);
          },
          onFailure: (err) => {
            // console.log('forgotPassword err', err);
            reject(err);
          },
          inputVerificationCode: (data) => {
            // onSuccess is not firing
            // console.log('callback that should not do anything data', data);
            resolve(true);
          }
        });
      } catch (ex) {
        reject(ex);
      }
    });
  }

  confirmPassword(email: string, verificationCode: string, newPassword: string) {
    // console.log('confirmPassword', email, verificationCode, newPassword);
    return new Promise((resolve, reject) => {
      try {
        let userData = {
          Username: email.toLowerCase(),
          Pool: this.api.userPool
        };
        this.cognitoUser = new AWSCognito.CognitoIdentityServiceProvider.CognitoUser(userData);
        this.cognitoUser.confirmPassword(verificationCode, newPassword, {
          onSuccess: (result) => {
            // console.log('confirmPassword result', result);
            resolve(true);
          },
          onFailure: (err) => {
            // console.log('confirmPassword err', err);
            reject(err);
          },
          inputVerificationCode: (data) => {
            // needs to be defined, but does not have to do anything
            // console.log('callback that should not do anything data', data);
          }
        });
      } catch (ex) {
        reject(ex);
      }
    });
  }

  private handleError(error: any) {
    console.error('An error occurred', error);
    return Promise.reject(error.message || error);
  }
}



// WEBPACK FOOTER //
// ./node_modules/angular-router-loader/src!./node_modules/angular2-template-loader!./node_modules/@angularclass/hmr-loader!./node_modules/tslint-loader!./src/app/shared/auth.service.ts